import { selectCurrentTeam } from 'containers/app/appSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isPermissionDenied } from 'utils/apiUtils';
import { useAppSelector } from '../../../hooks/typedHooks';
import { JobStatus, jobSearchTypes } from '../constant';
import { JobListItem } from '../jobs.model';
import { useLazyGetJobsQuery } from '../api';
import { APIErrorStatuses } from 'services/type';
import { selectJobsInit } from '../jobsSlice';
import { ColumnInfo } from 'containers/accounting/pay-runs/pay-period-review/payperiod.model';

export interface JobQueryParams {
  status: JobStatus[];
  createdBy: string[];
  customerIds: string[];
  assignees: string[];
  leadSource: string[];
  search: string;
  amount: string;
  scheduledFrom: string;
  scheduledTo: string;
  sortBy: string;
  sortOrder: string;
  isUnscheduled: boolean;
  groupByStatus: boolean;
  limit: number;
  serviceAreaId: string[];
  viewId?: string;
}

export const initialQueryParamsState = {
  status: [],
  createdBy: [],
  customerIds: [],
  assignees: [],
  leadSource: [],
  search: null,
  amount: null,
  scheduledFrom: null,
  scheduledTo: null,
  sortBy: 'scheduledFrom',
  sortOrder: 'DESC',
  isUnscheduled: false,
  groupByStatus: true,
  limit: 0,
  serviceAreaId: [],
  viewId: null,
  createdFrom: null,
  createdTo: null
};

export const emptyQueryParamsState = {
  status: [],
  createdBy: [],
  customerIds: [],
  assignees: [],
  leadSource: [],
  search: null,
  amount: null,
  scheduledFrom: null,
  scheduledTo: null,
  sortBy: 'scheduledFrom',
  sortOrder: 'DESC',
  isUnscheduled: false,
  groupByStatus: true,
  limit: 0,
  serviceAreaId: [],
  viewId: null,
  createdFrom: null,
  createdTo: null
};

export const usePaginatedGetJobsQuery = (jobLimit = 50) => {
  const [list, setList] = useState<JobListItem[]>([]);
  const [queryParams, setQueryParams] = useState<JobQueryParams>(initialQueryParamsState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const [columnConfig, setColumnConfig] = useState<ColumnInfo[]>([]);
  const page = useRef(1);
  const [getJobsData, jobsState] = useLazyGetJobsQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { groupByStatus: initialGroupByStatus, jobListPagination: initialLimit } = useAppSelector(selectJobsInit) ?? {
    groupByStatus: true,
    jobListPagination: 50
  };
  const [searchType, setSearchType] = useState(jobSearchTypes[0]);
  const isInitListCall: boolean = page.current === 1;
  const [groupedJobsByStatus, setGroupedJobsByStatus] = useState<Record<JobStatus, JobListItem[]>>(
    {} as Record<JobStatus, JobListItem[]>
  );

  useEffect(() => {
    if (!jobsState?.isFetching) {
      setHasPermissionsToView(true);
      if (page.current === 1) {
        setTotalListCount(jobsState?.data?.data.count ?? 0);
      }

      const jobs = jobsState?.data?.data?.jobList ?? [];
      setList(jobs);

      // Handle column configuration
      if (jobsState?.data?.data?.columnInfo) {
        const columnInfo = jobsState.data.data.columnInfo;
        if (columnInfo && Array.isArray(columnInfo) && columnInfo.length > 0) {
          const sortedColumnInfo = [...columnInfo].sort((a, b) => a.priority - b.priority);
          setColumnConfig(sortedColumnInfo);
        } else {
          setColumnConfig(columnInfo || []);
        }
      } else {
        setColumnConfig([]);
      }

      if (queryParams?.groupByStatus) {
        const grouped = jobs.reduce((acc, job) => {
          if (!acc[job.status]) acc[job.status] = [];
          acc[job.status].push(job);
          return acc;
        }, {} as Record<JobStatus, JobListItem[]>);
        setGroupedJobsByStatus(grouped);
      } else {
        setGroupedJobsByStatus({} as Record<JobStatus, JobListItem[]>);
      }

      setIsLoading(false);
    }
  }, [jobsState?.isFetching]);

  useEffect(() => {
    if (!jobsState?.isFetching && jobsState?.isError) {
      setIsLoading(false);
      if (isPermissionDenied(0 as APIErrorStatuses, jobsState.error?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
      setIsError(true);
    }
  }, [jobsState?.isError]);

  const resetListAndLoadMore = () => {
    setList([]);
    fetchData(1);
  };

  useEffect(() => {
    resetListAndLoadMore();
  }, [queryParams]);

  const fetchData = useCallback(
    currentPage => {
      setIsLoading(true);
      setIsError(false);
      const isSearchPresent = queryParams.search !== null && queryParams.search !== '';
      const { isUnscheduled, ...restQueryParams } = queryParams;
      page.current = currentPage;
      if (currentTeam?.id) {
        try {
          getJobsData({
            ...restQueryParams,
            teamId: currentTeam?.id,
            page: page.current - 1,
            limit: queryParams?.limit ? queryParams?.limit : jobLimit,
            search: null,
            [searchType.type]: isSearchPresent ? queryParams.search.trim() : null,
            ...(isUnscheduled ? { isUnscheduled: true } : {}),
            ...(queryParams?.groupByStatus ? { groupByStatus: true } : { groupByStatus: initialGroupByStatus }),
            ...(queryParams?.viewId ? { viewId: queryParams.viewId } : {})
          }).unwrap();
        } catch (err) {
          setIsError(true);
        }
      }
    },
    [currentTeam?.id, page.current, queryParams]
  );

  const resetQuery = () => {
    resetListAndLoadMore();
  };

  return {
    isLoading: isLoading,
    isFetching: jobsState?.isFetching,
    totalListCount,
    list,
    groupedJobsByStatus,
    isError,
    fetchData,
    resetQuery,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: jobsState.isSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType,
    page: page.current,
    limit: queryParams?.limit ?? initialLimit,
    columnConfig
  };
};
